import React, { memo, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
import { DataEntry } from '../bk-web-app/transaction-manager/types';
import {
  ButtonLink,
  Checkbox,
  checkboxStates,
  HighlightKeyword,
  Icon,
  TextBlock,
} from 'bench-internal-theme';
import { rgba } from 'color-helpers';
import FloatingExpandableText from './FloatingExpandableText';
import formattedCentsAmount from './formattedCentsAmount';
import { SelectedDataEntriesConsumer } from './SelectedDataEntriesContext';

// FIXME This file will be moved into the bk-web-app package in IN-2011
import CategorizationSuggestionsContainer from '../bk-web-app/transaction-manager/CategorizationSuggestionsContainer';
import { VendorDetailsTooltip } from './VendorDetailsTooltip';
import PotentialMatchesTooltip from './PotentialMatchesTooltip';
import { filteredMatchingTransactions } from './potentialMitoMatchFilter';
import { lookupAccount, formatSourceInfo } from './helpers';
import {
  getDataEntryRowHeight,
  BORDER_WIDTH,
  DATA_ENTRY_ROW_PADDING_HEIGHT,
  NOTE_ROW_GAP,
} from './getDataEntryRowHeight';
import { DataEntryRowProps } from './types';

/**
 * Converts a string in the form '2006-01-02' into the en-US representation without applying any
 * timezone offset, such as '1/2/2006'.
 */
const getFormattedDateString = dateString => {
  if (!dateString) {
    return '';
  }
  return new Date(dateString).toLocaleDateString('en-US', { timeZone: 'UTC' });
};

const DataEntryRowLayout = styled(TextBlock).attrs(() => ({ styleType: 'small' }))`
  background-color: ${colors.white};
  border-bottom: ${BORDER_WIDTH}px solid ${colors.silt};
  // Ensures there's a "top border", but doesn't change the height
  box-shadow: 0 -1px 0 ${colors.silt};
  cursor: pointer;
  display: flex;
  align-items: stretch;
  margin: 0 32px;
  position: relative;
  padding: 2px 0;
  min-height: 100%;
  height: 100%;
  &:hover {
    background-color: ${colors.brook};
  }

  ${({ isSelected }) => isSelected && `background-color: ${colors.brook}`};
  ${({ hasBackgroundColor }) => hasBackgroundColor && `background-color: ${colors.shell}`};
`;

const ExtendableColumn = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 8px;
`;

const Columns1099 = styled.div`
  display: flex;
  flex-basis: 80px;
  min-width: 80px;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 8px;
`;

const ContraLedgerColumn = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  max-width: 150px;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 8px;
`;

const ContraLedgerName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SourceColumn = styled.div<{ $hasContent?: boolean }>`
  flex-basis: 230px;
  min-width: 230px;
  max-width: 230px;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  cursor: ${props => (props.$hasContent ? 'help' : 'default')};

  &:focus {
    outline: 2px solid ${colors.creek};
    outline-offset: -2px;
    overflow: visible;
    white-space: normal;
    word-break: break-word;
    z-index: 1;
    background-color: ${colors.white};
  }

  &:hover {
    text-decoration: ${props => (props.$hasContent ? 'underline dotted' : 'none')};
  }
`;

const VendorName = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TransactionName = styled.div`
  flex: 1;
  min-width: 120px;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 8px;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const NotesColumn = styled(ExtendableColumn)`
  max-width: 300px;
`;

const NotesGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 24px auto;
  grid-column-gap: 5px;
  grid-row-gap: ${NOTE_ROW_GAP}px;
`;

const NotesLabelCell = styled.div`
  color: ${colors.dust};
  text-align: left;
`;

const NotesListCell = styled.div`
  min-width: 0;
`;

const IconColumn = styled.div<{ styleType?: string }>`
  align-items: flex-start;
  ${({ styleType }) =>
    styleType === 'warning' &&
    `
      background: ${rgba(colors.copper, 0.1)};
      box-shadow: inset 1px 0 0 ${colors.copper};
    `};
  display: flex;
  flex-basis: 30px;
  justify-content: center;
  min-width: 30px;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 0;
  padding-top: 8px;

  svg {
    display: block;
    margin-top: 0;
  }
`;

const ShowOnColumnHover = styled.span`
  display: none;

  ${IconColumn}:hover & {
    display: initial;
  }
`;

const HideOnColumnHover = styled.span`
  ${IconColumn}:hover & {
    display: none;
  }
`;

const AddOnColumn = styled.div`
  flex-basis: 100px;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 8px;
  min-width: 100px;
`;

const DateColumn = styled.div`
  color: ${colors.cloud};
  flex-basis: 70px;
  min-width: 70px;
  max-width: 70px;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 8px;
`;

const AmountColumn = styled.div<{ isDebit?: boolean }>`
  color: ${({ isDebit }) => (isDebit ? '#217950' : '#ae280e')};
  flex-basis: 80px;
  min-width: 80px;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 8px;
  text-align: right;
`;

const CategorizationSuggestionsColumn = styled.div`
  flex-basis: 208px;
  min-width: 208px;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 8px;
`;

const TooltipColumn = styled.div`
  flex-basis: 40px;
  text-align: center;
  padding: ${DATA_ENTRY_ROW_PADDING_HEIGHT}px 2px;
  min-width: 40px;
  height: auto;
`;

const BadgeIcon = styled.button<{ isHovered: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ isHovered }) => (isHovered ? 'red' : 'orange')};
  color: white;
  font-size: 12px;
  font-weight: bold;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-top: 5px;
  transition: background-color 0.3s ease;
  cursor: pointer;
  outline: none;
  border: none;
  padding: 0;

  &:hover {
    background-color: red;
  }

  &:focus {
    background-color: red;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8), 0 0 0 4px rgba(255, 0, 0, 0.5);
  }
`;

const TaskTooltip = styled.div`
  position: absolute;
  background-color: ${colors.white};
  border: 1px solid ${colors.silt};
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 12px;
  z-index: 1000;
  width: max-content;
  min-width: 160px;
  max-width: 280px;
  top: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  line-height: 1.4;

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${colors.silt};
  }

  &::after {
    content: '';
    position: absolute;
    top: -5px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid ${colors.white};
  }
`;

const TaskTooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TaskTooltipLabel = styled.span`
  color: ${colors.dust};
  font-weight: 500;
  white-space: nowrap;
`;

const TaskTooltipValue = styled.span`
  color: ${colors.charcoal};
  text-align: right;
  white-space: nowrap;
`;

const findContraLedgerName = (dataEntry: DataEntry) => {
  const { dataEntries } = dataEntry.journalEntry;
  if (dataEntry.journalEntry.dataEntries.length > 2) {
    return 'Split';
  }
  if (dataEntry.journalEntry.dataEntries.length <= 2) {
    const contraLedgerEntry = dataEntries.find(contraEntry => contraEntry.id !== dataEntry.id);
    return contraLedgerEntry && contraLedgerEntry.ledgerAccount
      ? contraLedgerEntry.ledgerAccount.name
      : '';
  }
  return null;
};

const formatCategorizerMethod = (categorizerMethod?: string): React.ReactNode => {
  switch (categorizerMethod) {
    case 'ML':
      return <Icon color={colors.dust} icon="globe" size="small" title="Global machine learning" />;
    case 'CLIENT_RULE':
      return <Icon color={colors.dust} icon="lock" size="small" title="Client rule" />;
    case 'GLOBAL_RULE':
      return <Icon color={colors.dust} icon="globe" size="small" title="Global rule" />;
    case 'CLIENT_BASED_ML':
      return (
        <Icon
          color={colors.dust}
          icon="circleUserOutline"
          size="small"
          title="Client based machine learning"
        />
      );
    default:
      return '';
  }
};

interface DataEntryRowComponent extends React.NamedExoticComponent<DataEntryRowProps> {
  getHeight?: typeof getDataEntryRowHeight;
}

const DataEntryRow: DataEntryRowComponent = memo(function DataEntryRow({
  hasBackgroundColor = 0,
  isAccount = false,
  isSelected = false,
  search,
  filterMito,
  isMoneyInTransitOutstanding = false,
  showCategorizationSuggestions = false,
  showLedgerAccountName = false,
  showSourceColumn = false,
  showTasksColumn = false,
  toggleDataEntry,
  client,
  dataEntry,
  index,
  matchingAmountTransactions = [],
  moneyInTransitLedgerId,
}: DataEntryRowProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isTaskTooltipVisible, setIsTaskTooltipVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const taskTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const potentialMatchRef = useRef<((action: 'match' | 'skip') => Promise<void>) | null>(null);

  const sourceInfo = useMemo(() => {
    if (!showSourceColumn) return '';
    return formatSourceInfo(dataEntry);
  }, [showSourceColumn, dataEntry]);

  const contraLedgerName = useMemo(() => {
    if (!isAccount) return '';
    return findContraLedgerName(dataEntry);
  }, [dataEntry, isAccount]);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current as ReturnType<typeof setTimeout>);
    setIsTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsTooltipVisible(false);
    }, 300);
  };

  const handleBadgeMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    handleMouseEnter();
    // Focus the badge when hovering to ensure keyboard events are captured
    e.currentTarget.focus();
  };

  const handleTaskTooltipMouseEnter = () => {
    clearTimeout(taskTooltipTimeoutRef.current as ReturnType<typeof setTimeout>);
    setIsTaskTooltipVisible(true);
  };

  const handleTaskTooltipMouseLeave = () => {
    taskTooltipTimeoutRef.current = setTimeout(() => {
      setIsTaskTooltipVisible(false);
    }, 300);
  };

  const { amountInCents, hasPotentialQualityIssues, id, journalEntry, ledgerAccount } = dataEntry;
  const {
    addOn,
    clientCatLedger,
    clientCatResponse,
    clientComments,
    dateOfTransaction,
    document,
    internalNotes,
    vendorName,
    contractorName,
    source1099,
    isTagged1099,
    id: transactionId,
    categorizerLedgerId,
  } = journalEntry;

  const hasAlternatingBackgroundColor = hasBackgroundColor || index % 2;
  const dataEntryAccount = lookupAccount(dataEntry);

  const matches = filteredMatchingTransactions({
    matchingAmountTransactions,
    dataEntryAccount,
    journalEntry,
  });

  const hasNotes = useMemo(
    () => clientComments.length > 0 || internalNotes.length > 0 || !!clientCatResponse,
    [clientComments.length, internalNotes.length, clientCatResponse],
  );

  const report1099Config = useMemo(
    () => client?.featureFlags?.report1099,
    [client?.featureFlags?.report1099],
  );

  if (!dataEntry?.journalEntry) {
    return null;
  }

  if (isMoneyInTransitOutstanding && filterMito && matches.length === 0) {
    return <></>; // Skip rendering this row if no matching transactions are found
  }

  return (
    <DataEntryRowLayout
      data-bypass-dismiss-sidebar
      hasBackgroundColor={hasAlternatingBackgroundColor}
      isSelected={isSelected}
      onMouseDown={() => toggleDataEntry(dataEntry)}
    >
      <IconColumn
        styleType={hasPotentialQualityIssues ? 'warning' : undefined}
        title={hasPotentialQualityIssues ? 'Potential quality issue' : undefined}
      >
        <SelectedDataEntriesConsumer>
          {({ isDataEntryIdSelected, addSelectedDataEntryIds, removeSelectedDataEntryIds }) => (
            <ButtonLink
              onMouseDown={mouseEvent => {
                // Prevent the sidebar from toggling
                mouseEvent.stopPropagation();
                if (isDataEntryIdSelected(id)) {
                  removeSelectedDataEntryIds(id);
                } else {
                  addSelectedDataEntryIds(id);
                }
              }}
            >
              {/* Unselected data entries with potential quality issues have a warning icon in
                this column and it turns into a checkbox when the column is hovered */}
              {!isDataEntryIdSelected(id) && hasPotentialQualityIssues ? (
                <>
                  <HideOnColumnHover>
                    <Icon color={colors.copper} icon="triangleExclamationMark" size="small" />
                  </HideOnColumnHover>
                  <ShowOnColumnHover>
                    <Checkbox checkboxState={checkboxStates.UNCHECKED} />
                  </ShowOnColumnHover>
                </>
              ) : (
                <Checkbox
                  checkboxState={
                    isDataEntryIdSelected(id) ? checkboxStates.CHECKED : checkboxStates.UNCHECKED
                  }
                />
              )}
            </ButtonLink>
          )}
        </SelectedDataEntriesConsumer>
      </IconColumn>
      <div style={{ width: matches.length > 0 ? '20px' : '12px' }}>
        {matches.length > 0 && (
          <>
            <BadgeIcon
              isHovered={isTooltipVisible}
              title="Click to confirm, right-click to skip"
              tabIndex={0}
              onMouseDown={async e => {
                e.stopPropagation();
                await potentialMatchRef.current?.('match');
              }}
              onKeyDown={async e => {
                if (e.key === ' ' || e.key === 'Spacebar') {
                  e.preventDefault();
                  e.stopPropagation();
                  await potentialMatchRef.current?.('skip');
                }
              }}
              onMouseEnter={handleBadgeMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {matches.length}
            </BadgeIcon>
            {isTooltipVisible && (
              <PotentialMatchesTooltip
                transactions={matches}
                parentTransaction={{
                  id: dataEntry.id,
                  accountName: dataEntryAccount?.name || null,
                  transactionDate: dateOfTransaction,
                }}
                moneyInTransitLedgerId={moneyInTransitLedgerId}
                onActionRef={potentialMatchRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            )}
          </>
        )}
      </div>
      <DateColumn>{getFormattedDateString(dateOfTransaction)}</DateColumn>
      <TransactionName>
        <HighlightKeyword keyword={search}>{vendorName || ''}</HighlightKeyword>
        {showLedgerAccountName && ledgerAccount && ` — ${ledgerAccount.name}`}
      </TransactionName>
      <TooltipColumn>
        {transactionId && <VendorDetailsTooltip transactionId={transactionId} />}
      </TooltipColumn>
      {isAccount && (
        <ContraLedgerColumn>
          <ContraLedgerName>{contraLedgerName}</ContraLedgerName>
        </ContraLedgerColumn>
      )}
      {showSourceColumn && (
        <SourceColumn
          title={sourceInfo}
          $hasContent={!!sourceInfo}
          tabIndex={sourceInfo ? 0 : -1}
          role={sourceInfo ? 'button' : undefined}
          aria-label={sourceInfo ? `Source: ${sourceInfo}` : undefined}
        >
          {sourceInfo}
        </SourceColumn>
      )}
      <AmountColumn isDebit>
        {amountInCents >= 0 && formattedCentsAmount(Math.abs(amountInCents))}
      </AmountColumn>
      <AmountColumn>
        {amountInCents < 0 && formattedCentsAmount(Math.abs(amountInCents))}
      </AmountColumn>
      {showCategorizationSuggestions && (
        <CategorizationSuggestionsColumn
          onMouseDown={mouseEvent => {
            // Prevent the sidebar from toggling
            mouseEvent.stopPropagation();
          }}
        >
          <CategorizationSuggestionsContainer dataEntry={dataEntry} />
        </CategorizationSuggestionsColumn>
      )}
      {report1099Config && (
        <>
          <Columns1099>
            <VendorName>
              {isTagged1099 ? (contractorName ? contractorName : 'Unknown') : ''}
            </VendorName>
          </Columns1099>
          <Columns1099>
            <VendorName>{isTagged1099 ? (source1099 ? source1099 : 'Unknown') : ''}</VendorName>
          </Columns1099>
        </>
      )}
      <NotesColumn>
        {hasNotes && (
          <NotesGrid>
            {clientComments.length > 0 && (
              <>
                <NotesLabelCell title="Client Comments">CC:</NotesLabelCell>
                <NotesListCell>
                  {clientComments.map(({ body, createdOn }) => (
                    <FloatingExpandableText key={`${body}-${createdOn}`}>
                      <HighlightKeyword keyword={search}>{body}</HighlightKeyword>
                    </FloatingExpandableText>
                  ))}
                </NotesListCell>
              </>
            )}
            {!!clientCatResponse && (
              <>
                <NotesLabelCell title="Client Categorization Note">CN:</NotesLabelCell>
                <NotesListCell>
                  <FloatingExpandableText>
                    <HighlightKeyword keyword={search}>{clientCatResponse}</HighlightKeyword>
                  </FloatingExpandableText>
                </NotesListCell>
              </>
            )}
            {internalNotes.length > 0 && (
              <>
                <NotesLabelCell title="Internal Notes">IN:</NotesLabelCell>
                <NotesListCell>
                  {internalNotes.map(({ body, createdOn }) => (
                    <FloatingExpandableText key={`${body}=${createdOn}`}>
                      <HighlightKeyword keyword={search}>{body}</HighlightKeyword>
                    </FloatingExpandableText>
                  ))}
                </NotesListCell>
              </>
            )}
          </NotesGrid>
        )}
      </NotesColumn>
      <IconColumn title="Has client comments, client categorization response, or internal notes">
        {hasNotes && <Icon color={colors.dust} icon="speechBubble" size="small" />}
      </IconColumn>
      <IconColumn title="Categorization Method">
        {clientCatLedger?.id === ledgerAccount?.id ? (
          <Icon
            color={colors.dust}
            icon="circleUser"
            size="small"
            title="Client selected this ledger account"
          />
        ) : categorizerLedgerId === ledgerAccount?.id ? (
          formatCategorizerMethod(journalEntry.categorizerMethod)
        ) : (
          ''
        )}
      </IconColumn>
      <IconColumn title="Has attachment">
        {document && <Icon color={colors.dust} icon="paperclip" size="small" />}
      </IconColumn>
      <IconColumn title="Has flagged internal note(s)">
        {internalNotes.some(({ flagged }) => flagged) && (
          <Icon color={colors.dust} icon="flagFilled" size="small" />
        )}
      </IconColumn>
      {showTasksColumn && (
        <IconColumn
          style={{ position: 'relative' }}
          onMouseEnter={dataEntry.task ? handleTaskTooltipMouseEnter : undefined}
          onMouseLeave={dataEntry.task ? handleTaskTooltipMouseLeave : undefined}
        >
          {dataEntry.task && (
            <div style={{ position: 'relative', alignSelf: 'flex-start' }}>
              <Icon
                color={dataEntry.task.status === 'PENDING_WORK' ? colors.copper : colors.bonsai}
                icon={dataEntry.task.status === 'PENDING_WORK' ? 'clock' : 'circleCheck'}
                size="20px"
              />
              {isTaskTooltipVisible && (
                <TaskTooltip>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: colors.charcoal }}>
                    {dataEntry.task.status === 'PENDING_WORK'
                      ? 'Awaiting confirmation'
                      : 'Client confirmed'}
                  </div>
                  <TaskTooltipRow>
                    <TaskTooltipLabel>Created:</TaskTooltipLabel>
                    <TaskTooltipValue>
                      {dataEntry.task?.createdOn
                        ? getFormattedDateString(dataEntry.task.createdOn)
                        : 'N/A'}
                    </TaskTooltipValue>
                  </TaskTooltipRow>
                  {dataEntry.task?.resolvedOn && (
                    <TaskTooltipRow>
                      <TaskTooltipLabel>Confirmed:</TaskTooltipLabel>
                      <TaskTooltipValue>
                        {getFormattedDateString(dataEntry.task.resolvedOn)}
                      </TaskTooltipValue>
                    </TaskTooltipRow>
                  )}
                  {dataEntry.task?.createdBy && (
                    <TaskTooltipRow>
                      <TaskTooltipLabel>Created by:</TaskTooltipLabel>
                      <TaskTooltipValue>{dataEntry.task.createdBy}</TaskTooltipValue>
                    </TaskTooltipRow>
                  )}
                </TaskTooltip>
              )}
            </div>
          )}
        </IconColumn>
      )}
      {showCategorizationSuggestions ? (
        <IconColumn title="Has Add-On">
          {addOn && <Icon color={colors.dust} icon="link" size="small" />}
        </IconColumn>
      ) : (
        <AddOnColumn>
          <FloatingExpandableText>{addOn && addOn.name}</FloatingExpandableText>
        </AddOnColumn>
      )}
    </DataEntryRowLayout>
  );
});

// Expose static height calculation as a property of the component
DataEntryRow.getHeight = getDataEntryRowHeight;

export default DataEntryRow;
